<template>
  <DashboardLayout>
    <div class="max-w-3xl mx-auto">
      <!-- Header -->
      <div class="mb-6">
        <Link
          :href="route('settings.tickets.sla.index')"
          class="text-sm text-gray-600 hover:text-gray-900 flex items-center mb-4"
        >
          <ArrowLeftIcon class="h-4 w-4 mr-1" />
          Back to SLA Policies
        </Link>
        <h1 class="text-3xl font-bold text-gray-900">
          {{ isEditMode ? 'Edit SLA Policy' : 'Create New SLA Policy' }}
        </h1>
        <p class="text-gray-600 mt-1">
          {{ isEditMode ? 'Update SLA policy information' : 'Define service level agreement targets' }}
        </p>
      </div>

      <form @submit.prevent="submitForm">
        <div class="space-y-6">
          <!-- Basic Information -->
          <Card>
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div class="space-y-4">
              <!-- Name -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Policy Name <span class="text-red-500">*</span>
                </label>
                <Input
                  v-model="form.name"
                  placeholder="e.g., Standard Support, Enterprise SLA"
                  :error="form.errors.name"
                  maxlength="100"
                />
                <p v-if="form.errors.name" class="mt-1 text-sm text-red-600">
                  {{ form.errors.name }}
                </p>
              </div>

              <!-- Priority Level -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Priority Level <span class="text-red-500">*</span>
                </label>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <button
                    v-for="priority in priorityOptions"
                    :key="priority.value"
                    type="button"
                    @click="form.priority = priority.value"
                    class="relative p-4 border-2 rounded-lg text-center transition"
                    :class="[
                      form.priority === priority.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    ]"
                  >
                    <component
                      :is="priority.icon"
                      class="h-6 w-6 mx-auto mb-2"
                      :class="priority.color"
                    />
                    <span class="text-sm font-medium">{{ priority.label }}</span>
                    <CheckCircleIcon
                      v-if="form.priority === priority.value"
                      class="absolute top-2 right-2 h-5 w-5 text-primary-500"
                    />
                  </button>
                </div>
                <p class="text-xs text-gray-500 mt-2">
                  This policy will apply to all tickets with this priority level
                </p>
                <p v-if="form.errors.priority" class="mt-1 text-sm text-red-600">
                  {{ form.errors.priority }}
                </p>
              </div>
            </div>
          </Card>

          <!-- Time Targets -->
          <Card>
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Time Targets</h2>
            <div class="space-y-6">
              <!-- Response Time -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Response Time <span class="text-red-500">*</span>
                </label>
                <p class="text-xs text-gray-500 mb-3">
                  Time before first response must be provided
                </p>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1">Hours</label>
                    <Input
                      v-model.number="responseHours"
                      type="number"
                      min="0"
                      placeholder="0"
                      @input="updateResponseTime"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1">Minutes</label>
                    <Input
                      v-model.number="responseMinutes"
                      type="number"
                      min="0"
                      max="59"
                      placeholder="0"
                      @input="updateResponseTime"
                    />
                  </div>
                </div>
                <div class="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p class="text-sm font-medium text-blue-900">
                    Total: {{ formatTime(form.response_time_minutes) }}
                  </p>
                </div>
                <p v-if="form.errors.response_time_minutes" class="mt-1 text-sm text-red-600">
                  {{ form.errors.response_time_minutes }}
                </p>
              </div>

              <!-- Resolution Time -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Resolution Time <span class="text-red-500">*</span>
                </label>
                <p class="text-xs text-gray-500 mb-3">
                  Time before ticket must be resolved
                </p>
                <div class="grid grid-cols-3 gap-4">
                  <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1">Days</label>
                    <Input
                      v-model.number="resolutionDays"
                      type="number"
                      min="0"
                      placeholder="0"
                      @input="updateResolutionTime"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1">Hours</label>
                    <Input
                      v-model.number="resolutionHours"
                      type="number"
                      min="0"
                      max="23"
                      placeholder="0"
                      @input="updateResolutionTime"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1">Minutes</label>
                    <Input
                      v-model.number="resolutionMinutes"
                      type="number"
                      min="0"
                      max="59"
                      placeholder="0"
                      @input="updateResolutionTime"
                    />
                  </div>
                </div>
                <div class="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p class="text-sm font-medium text-green-900">
                    Total: {{ formatTime(form.resolution_time_minutes) }}
                  </p>
                </div>
                <p v-if="form.errors.resolution_time_minutes" class="mt-1 text-sm text-red-600">
                  {{ form.errors.resolution_time_minutes }}
                </p>
              </div>

              <!-- Business Hours Only -->
              <div>
                <div class="flex items-start space-x-3">
                  <input
                    v-model="form.business_hours_only"
                    type="checkbox"
                    class="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <label class="block text-sm font-medium text-gray-900">
                      Calculate during business hours only
                    </label>
                    <p class="text-sm text-gray-500 mt-1">
                      SLA timers will pause outside of business hours (typically Mon-Fri, 9am-5pm)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <!-- Escalation Settings -->
          <Card>
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Escalation Settings</h2>
            <div class="space-y-4">
              <!-- Escalate if Breached -->
              <div>
                <div class="flex items-start space-x-3">
                  <input
                    v-model="form.escalate_if_breached"
                    type="checkbox"
                    class="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <label class="block text-sm font-medium text-gray-900">
                      Enable escalation when SLA is breached
                    </label>
                    <p class="text-sm text-gray-500 mt-1">
                      Automatically notify designated user when SLA targets are not met
                    </p>
                  </div>
                </div>
              </div>

              <!-- Escalation User -->
              <div v-if="form.escalate_if_breached">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Escalation User
                </label>
                <Select
                  v-model="form.escalation_user_id"
                  :options="userOptions"
                  placeholder="Select user to notify"
                  :error="form.errors.escalation_user_id"
                >
                  <template #selected v-if="form.escalation_user_id">
                    <div class="flex items-center space-x-2">
                      <Avatar
                        :src="getSelectedUser(form.escalation_user_id)?.profile_photo"
                        :name="getSelectedUser(form.escalation_user_id)?.full_name"
                        size="xs"
                      />
                      <span>{{ getSelectedUser(form.escalation_user_id)?.full_name }}</span>
                    </div>
                  </template>
                </Select>
                <p class="text-xs text-gray-500 mt-1">
                  This user will receive notifications when tickets breach SLA
                </p>
                <p v-if="form.errors.escalation_user_id" class="mt-1 text-sm text-red-600">
                  {{ form.errors.escalation_user_id }}
                </p>
              </div>
            </div>
          </Card>

          <!-- Status -->
          <Card>
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-sm font-medium text-gray-900">Active</h3>
                <p class="text-sm text-gray-500">
                  Inactive policies won't be applied to tickets
                </p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  v-model="form.is_active"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"
                ></div>
              </label>
            </div>
          </Card>

          <!-- Preview -->
          <Card>
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Preview</h2>
            <div class="bg-gray-50 rounded-lg p-4 space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-700">Policy Name:</span>
                <span class="text-sm text-gray-900">{{ form.name || 'Untitled Policy' }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-700">Priority:</span>
                <Badge :variant="getPriorityVariant(form.priority)">
                  {{ formatPriority(form.priority) }}
                </Badge>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-700">Response Time:</span>
                <span class="text-sm font-semibold text-blue-600">
                  {{ formatTime(form.response_time_minutes) }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-700">Resolution Time:</span>
                <span class="text-sm font-semibold text-green-600">
                  {{ formatTime(form.resolution_time_minutes) }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-700">Calculation:</span>
                <span class="text-sm text-gray-900">
                  {{ form.business_hours_only ? 'Business Hours Only' : '24/7' }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-700">Escalation:</span>
                <span class="text-sm text-gray-900">
                  {{ form.escalate_if_breached ? 'Enabled' : 'Disabled' }}
                </span>
              </div>
            </div>
          </Card>

          <!-- Actions -->
          <Card>
            <div class="flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                @click="handleCancel"
              >
                Cancel
              </Button>
              <div class="flex space-x-3">
                <Button
                  v-if="isEditMode"
                  type="button"
                  variant="outline"
                  @click="showDeleteModal = true"
                  :disabled="form.processing"
                >
                  <TrashIcon class="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <Button
                  type="submit"
                  :loading="form.processing"
                  :disabled="!isFormValid"
                >
                  <CheckIcon class="h-5 w-5 mr-2" />
                  {{ isEditMode ? 'Update Policy' : 'Create Policy' }}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </form>
    </div>

    <!-- Delete Confirmation Modal -->
    <Modal :show="showDeleteModal" @close="showDeleteModal = false" max-width="md">
      <template #title>Delete SLA Policy</template>
      <template #content>
        <p class="text-sm text-gray-600">
          Are you sure you want to delete this policy? This action cannot be undone.
        </p>
      </template>
      <template #footer>
        <div class="flex justify-end space-x-3">
          <Button variant="outline" @click="showDeleteModal = false">Cancel</Button>
          <Button variant="danger" @click="deletePolicy" :loading="deleteLoading">
            Delete Policy
          </Button>
        </div>
      </template>
    </Modal>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { router, Link, useForm } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Badge from '@/Components/Common/Badge.vue';
import Input from '@/Components/Common/Input.vue';
import Select from '@/Components/Common/Select.vue';
import Avatar from '@/Components/Common/Avatar.vue';
import Modal from '@/Components/Common/Modal.vue';
import {
  ArrowLeftIcon,
  CheckIcon,
  CheckCircleIcon,
  TrashIcon,
  FlagIcon,
  MinusCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/vue/24/outline';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
  policy: {
    type: Object,
    default: null,
  },
  users: {
    type: Array,
    default: () => [],
  },
});

const { showToast } = useToast();

// State
const showDeleteModal = ref(false);
const deleteLoading = ref(false);

// Time breakdown state
const responseHours = ref(0);
const responseMinutes = ref(0);
const resolutionDays = ref(0);
const resolutionHours = ref(0);
const resolutionMinutes = ref(0);

// Computed
const isEditMode = computed(() => !!props.policy);

// Form
const form = useForm({
  name: props.policy?.name || '',
  priority: props.policy?.priority || 'normal',
  response_time_minutes: props.policy?.response_time_minutes || 60,
  resolution_time_minutes: props.policy?.resolution_time_minutes || 480,
  business_hours_only: props.policy?.business_hours_only ?? true,
  escalate_if_breached: props.policy?.escalate_if_breached ?? false,
  escalation_user_id: props.policy?.escalation_user_id || null,
  is_active: props.policy?.is_active ?? true,
});

// Initialize time breakdown
if (props.policy) {
  responseHours.value = Math.floor(props.policy.response_time_minutes / 60);
  responseMinutes.value = props.policy.response_time_minutes % 60;

  resolutionDays.value = Math.floor(props.policy.resolution_time_minutes / 1440);
  const remainingMinutes = props.policy.resolution_time_minutes % 1440;
  resolutionHours.value = Math.floor(remainingMinutes / 60);
  resolutionMinutes.value = remainingMinutes % 60;
}

// Options
const priorityOptions = [
  {
    value: 'low',
    label: 'Low',
    icon: MinusCircleIcon,
    color: 'text-gray-500',
  },
  {
    value: 'normal',
    label: 'Normal',
    icon: ExclamationCircleIcon,
    color: 'text-blue-500',
  },
  {
    value: 'high',
    label: 'High',
    icon: ExclamationTriangleIcon,
    color: 'text-orange-500',
  },
  {
    value: 'urgent',
    label: 'Urgent',
    icon: FlagIcon,
    color: 'text-red-500',
  },
];

const userOptions = computed(() => [
  { value: null, label: 'Select user' },
  ...props.users.map(user => ({
    value: user.id,
    label: user.full_name,
  })),
]);

const isFormValid = computed(() => {
  return (
    form.name.trim().length > 0 &&
    form.priority &&
    form.response_time_minutes > 0 &&
    form.resolution_time_minutes > 0 &&
    (!form.escalate_if_breached || form.escalation_user_id)
  );
});

// Methods
const updateResponseTime = () => {
  form.response_time_minutes = (responseHours.value * 60) + responseMinutes.value;
};

const updateResolutionTime = () => {
  form.resolution_time_minutes =
    (resolutionDays.value * 1440) +
    (resolutionHours.value * 60) +
    resolutionMinutes.value;
};

const formatTime = (minutes) => {
  if (!minutes || minutes === 0) return '0 minutes';

  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours < 24) {
    const hoursStr = `${hours} hour${hours !== 1 ? 's' : ''}`;
    const minutesStr = remainingMinutes > 0 ? ` ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}` : '';
    return hoursStr + minutesStr;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  const daysStr = `${days} day${days !== 1 ? 's' : ''}`;
  const hoursStr = remainingHours > 0 ? ` ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}` : '';
  return daysStr + hoursStr;
};

const formatPriority = (priority) => {
  const labels = {
    low: 'Low',
    normal: 'Normal',
    high: 'High',
    urgent: 'Urgent',
  };
  return labels[priority] || priority;
};

const getPriorityVariant = (priority) => {
  const variants = {
    low: 'neutral',
    normal: 'info',
    high: 'warning',
    urgent: 'danger',
  };
  return variants[priority] || 'neutral';
};

const getSelectedUser = (userId) => {
  return props.users.find(u => u.id === userId);
};

const submitForm = () => {
  const url = isEditMode.value
    ? route('settings.tickets.sla.update', props.policy.id)
    : route('settings.tickets.sla.store');

  const method = isEditMode.value ? 'put' : 'post';

  form[method](url, {
    preserveState: true,
    onSuccess: () => {
      showToast({
        type: 'success',
        message: isEditMode.value
          ? 'Policy updated successfully.'
          : 'Policy created successfully.',
      });
      router.visit(route('settings.tickets.sla.index'));
    },
    onError: () => {
      showToast({
        type: 'error',
        message: 'Please fix the errors and try again.',
      });
    },
  });
};

const deletePolicy = () => {
  if (!props.policy) return;

  deleteLoading.value = true;

  router.delete(route('settings.tickets.sla.destroy', props.policy.id), {
    onSuccess: () => {
      showToast({
        type: 'success',
        message: 'Policy deleted successfully.',
      });
      router.visit(route('settings.tickets.sla.index'));
    },
    onError: () => {
      showToast({
        type: 'error',
        message: 'Failed to delete policy.',
      });
      deleteLoading.value = false;
    },
  });
};

const handleCancel = () => {
  router.visit(route('settings.tickets.sla.index'));
};

// Watch for escalation toggle
watch(() => form.escalate_if_breached, (newValue) => {
  if (!newValue) {
    form.escalation_user_id = null;
  }
});
</script>
